/**
 * Registry documents — official company documents ordered from Armadillo
 * (RegDocs) for a report's company subjects.
 *
 * Mirrors `vnext/schemas/registry_document.py`. The wire shapes are
 * `Api*`-prefixed and converted by `transformTo*`, as everywhere else in
 * `src/types/`.
 *
 * An order is *prepared* on the report while it is in triage and sent to
 * Armadillo when the report is activated (Start); a report already
 * activated has it sent at once. Delivery is by email into a mailbox the
 * delivery worker reads, so documents appear on the order minutes to days
 * later depending on the product's turnaround.
 *
 * The research desk also answers by email without a document: a quote, a
 * question, a cancellation confirmed. Those are an order's *replies*. A
 * person answers them from the order here: the reply is sent as the shared
 * mailbox and recorded on the order as an *outbound* message. An order
 * that will not be completed is then *closed* here, which cancels it.
 */

import { JURISDICTIONS } from '@/constants';
import type { Country } from './country';
import type { ReportStatus } from './report';

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------

export type RegistryOrderStatus =
  | 'prepared'
  | 'pending'
  | 'placed'
  | 'partially_delivered'
  | 'delivered'
  | 'failed'
  | 'timed_out'
  | 'cancelled';

/** Why a person closed an order that was never completed. */
export type RegistryOrderCloseReason =
  | 'quote_declined'
  | 'cancelled_with_provider'
  | 'not_received_by_provider'
  | 'other';

/** One company a search returned. `company_id` is Armadillo's identifier
 * (the D-U-N-S number) — what an order names. */
export interface ApiRegistryCompanyCandidate {
  company_id: string;
  name: string;
  country_code: string | null;
  address: string | null;
  registration_numbers: string[];
  is_branch: boolean | null;
  is_out_of_business: boolean | null;
  /** Which search arm produced it: duns, registration_number, url, email
   * or name — an identifier hit is a stronger match than a name hit. */
  source_arm: string;
}

export interface ApiRegistryCompanySearchResponse {
  candidates: ApiRegistryCompanyCandidate[];
  query_arms: { kind: string; hits: number }[];
  matched_results: number | null;
  page: number;
}

export interface ApiRegistryProduct {
  code: string;
  description: string | null;
  /** Armadillo's turnaround estimate ("2 Hours", "2-5 Days", …) or "Quote". */
  availability: string | null;
  /** Decimal on the wire (a string); null when priced on quote. */
  price: string | number | null;
  currency: string | null;
  language: string | null;
  requires_quote: boolean;
}

export interface ApiRegistryProductsResponse {
  country: string;
  products: ApiRegistryProduct[];
}

/** The research desk's quote, as a person recorded it: with a reply, or
 * on its own for a quote accepted outside the Hub. */
export interface ApiRegistryOrderQuote {
  /** Decimal on the wire (a string). */
  price: string | number | null;
  currency: string | null;
  turnaround_days: number | null;
  recorded_at: string | null;
  recorded_by_user_id: string | null;
  /** A note left when the quote was recorded on its own; absent on a
   * quote recorded with a reply and on an API that predates the field. */
  note?: string | null;
  /** The desk message the quote answers, when it was recorded on its own
   * against one; absent as `note` is. */
  answers_message_id?: string | null;
}

/** How far an extraction of information from an order's delivered
 * documents has got: still reading, or how it ended. */
export type RegistryDigestRunStatus = 'running' | 'completed' | 'failed';

/** What a delivered document is read as, which decides the section it
 * feeds: `corporate` (extracts, registers, lists of officers or
 * shareholders) feeds Corporate Information, `financial` (filed accounts)
 * Financial Information, `document` (a certificate of good standing,
 * articles ...) a section of its own, and `trace` (the desk's trace
 * report, sent when the document could not be obtained) is not read and
 * gets a notice line. Mirrors `DocumentCoverage.role`. */
export const REGISTRY_DIGEST_DOCUMENT_ROLES = [
  'corporate',
  'financial',
  'document',
  'trace',
] as const;
export type RegistryDigestDocumentRole = (typeof REGISTRY_DIGEST_DOCUMENT_ROLES)[number];

/** How much of one delivered document the extraction read. */
export interface ApiRegistryDigestDocumentCoverage {
  filename: string | null;
  product_code: string | null;
  pages_total: number;
  pages_read: number;
  /** Pages without a text layer, read as images. */
  pages_vision: number;
  /** Pages past the caps were dropped: the tables may be incomplete. */
  truncated: boolean;
  /** What the document was read as. Null on a digest that predates roles
   * (its documents were read as corporate); absent, like `title`, on an
   * API that predates the field. A trace is listed with no page read. */
  role?: RegistryDigestDocumentRole | null;
  /** The section title of a financial or other document: the catalogue's
   * name for the product, else the code or the filename's document part. */
  title?: string | null;
}

/** The extraction's state on an order: what the button and the status
 * line show. Mirrors `RegistryDocumentDigestStatus`. */
export interface ApiRegistryDigestStatus {
  status: RegistryDigestRunStatus;
  requested_at: string | null;
  requested_by_user_id: string | null;
  started_at: string | null;
  finished_at: string | null;
  /** Failed only: the error's type and a short message, never document text. */
  error: string | null;
  /** The model that read the documents. */
  model: string | null;
  documents: ApiRegistryDigestDocumentCoverage[];
  /** How many model calls the documents were split into, and how many of
   * them failed and were skipped. */
  chunks: number;
  chunks_failed: number;
  /** The last insertion of the digest into the report (its Word file or
   * its Hub-written markdown); absent on an API that predates "Add to
   * report", null until the first add. */
  added_to_report?: ApiRegistryDigestAddedToReport | null;
}

/** Where the order's block went in the report: in place of the block an
 * earlier add left, under the report's existing "Registry documents"
 * section, before its appendix or sources heading, before the footer of
 * a Hub-written report, or at the end. */
export type RegistryDigestPlacement =
  | 'replaced_block'
  | 'inside_section'
  | 'before_appendix'
  | 'before_footer'
  | 'end';

/** The record of the digest's last insertion, on the extraction's state. */
export interface ApiRegistryDigestAddedToReport {
  /** The report content version the insertion created. */
  version: number;
  at?: string | null;
  by_user_id?: string | null;
  placement?: string | null;
  /** The block in the report is up to date: the latest content version
   * still carries it (`present`) and the run added is the run the digest
   * serves now. */
  current: boolean;
  /** The latest content version still carries this order's block. False
   * once a later version (a regeneration, a rewrite through feedback)
   * dropped it. Absent on an API that predates the check, which never
   * dropped a block. */
  present?: boolean | null;
  /** The content version `present` was last decided against; absent on
   * an API that predates the check, when it is the version added. */
  checked_version?: number | null;
}

/** The report's document as the order's detail describes it: what decides
 * whether the digest can be added, and to which stage of report. */
export interface ApiRegistryReportContent {
  kind?: 'docx' | 'markdown' | null;
  version?: number | null;
  lifecycle_stage: string;
  approval_required?: boolean | null;
  approval_status?: string | null;
}

export interface ApiRegistryOrder {
  id: string;
  report_id: string;
  subject_id: string | null;
  team_id: string;
  provider: string;
  company_id: string | null;
  manual: boolean;
  company_name: string;
  country_code: string;
  products: string[];
  reference: string;
  notes: string | null;
  provider_order_id: string | null;
  status: RegistryOrderStatus;
  expected_document_count: number;
  received_document_count: number;
  ordered_by_user_id: string | null;
  placed_at: string | null;
  completed_at: string | null;
  failure_reason: string | null;
  list_price: string | number | null;
  currency: string | null;
  expected_at: string | null;
  requires_quote: boolean;
  placement_attempts: number;
  /** The status the delivery worker's verdict would give the order.
   * Recorded while the worker is not allowed to move the status itself
   * (shadow mode); absent on an API that predates the field. */
  proposed_status?: RegistryOrderStatus | null;
  /** Emails from the desk that stored no new document and that nobody has
   * answered yet. Absent, like the three fields below, on an API that
   * predates them. */
  reply_count?: number;
  /** When the latest such email was received. */
  last_reply_at?: string | null;
  /** The close reason; null unless a person closed the order. */
  closed_reason?: string | null;
  closed_by_user_id?: string | null;
  /** The quote recorded with a reply; null until then, absent on an API
   * that predates replying. */
  quote?: ApiRegistryOrderQuote | null;
  /** The state of the extraction of information from the delivered
   * documents; null until one was started, absent on an API that predates
   * it. */
  digest?: ApiRegistryDigestStatus | null;
  /** The partner assignment that bills the order; null until one does,
   * absent like the two fields below on an API that predates Armadillo
   * as a network partner. */
  backoffice_assignment_id?: string | null;
  /** What the order adds to Armadillo's partner fee, GBP with pence;
   * null while the order is only prepared. */
  partner_fee?: string | number | null;
  partner_fee_source?: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** Where an order's share of Armadillo's partner fee comes from. Mirrors
 * `FeeSource` in the API's `registry_documents/partner_assignment.py`. */
export type RegistryFeeSource =
  | 'list_price'
  | 'quote'
  | 'awaiting_quote'
  | 'not_gbp'
  | 'not_billable';

export interface ApiRegistryCatalogueLine {
  code: string;
  description?: string | null;
  availability?: string | null;
  price?: string | number | null;
  currency?: string | null;
  requires_quote?: boolean;
}

export interface ApiRegistryDeliveredFile {
  sha256: string;
  filename: string | null;
  kind: string;
  size_bytes: number;
  content_type: string;
  product_code: string | null;
  reason: string | null;
  message_id: string | null;
  stored_at: string | null;
  download_url: string | null;
}

export interface ApiRegistryManualDetails {
  registration_number: string | null;
  address: string | null;
  town: string | null;
  postcode: string | null;
}

export type RegistryOrderMessageDirection = 'inbound' | 'outbound';

/** How an inbound email found its order: the GTI reference it carried, the
 * Gmail thread it continued, or a person attaching it by hand. */
export type RegistryOrderMessageMatch = 'reference' | 'thread' | 'manual';

/** One email recorded on an order. `text` is the stored body excerpt:
 * plain text chosen by the sender, never markup to render. */
export interface ApiRegistryOrderMessage {
  /** When the mailbox received it; for an outbound message the API fills
   * it with `sent_at`, so one list sorts by time. */
  received_at?: string | null;
  sender?: string | null;
  text?: string | null;
  truncated?: boolean;
  /** The files this email added to the order. */
  new_documents?: number | unknown[] | null;
  /** False when the strict rules refused the sender and the attachments
   * were withheld. */
  sender_verified?: boolean;
  /** The correlator's reason values. */
  reasons?: string[] | null;
  /** The RFC Message-ID; what a reply names in `in_reply_to`. */
  message_id?: string | null;
  /** Absent on an API that predates replying: every message was inbound. */
  direction?: RegistryOrderMessageDirection | null;
  /** Outbound only: who sent it, and when. */
  sent_by_user_id?: string | null;
  sent_at?: string | null;
  /** Outbound only: the inbound `message_id` it answers. */
  in_reply_to?: string | null;
  /** Inbound only: a reply names it, or any reply was sent after it. */
  answered?: boolean;
  /** Inbound only: ISO-2 codes of the countries the email names. Absent on
   * an API that predates the country cross-check. */
  countries_mentioned?: string[] | null;
  /** Inbound only: who attached the email by hand, when it was. */
  attached_by_user_id?: string | null;
  /** Inbound only: how the email found its order. */
  matched_by?: RegistryOrderMessageMatch | null;
}

export interface ApiRegistryOrderDetail extends ApiRegistryOrder {
  provider_response: Record<string, unknown> | null;
  catalogue_snapshot: ApiRegistryCatalogueLine[] | null;
  manual_details: ApiRegistryManualDetails | null;
  delivery_metadata: Record<string, unknown> | null;
  documents: ApiRegistryDeliveredFile[];
  /** Every email recorded on the order, oldest first; absent on an API
   * that predates the field. */
  messages?: ApiRegistryOrderMessage[] | null;
  /** The report's document, for "Add to report"; absent on an API that
   * predates it, null when the order's report is missing. */
  report_content?: ApiRegistryReportContent | null;
}

export interface ApiRegistryOrderList {
  report_id: string;
  orders: ApiRegistryOrder[];
  version: string;
}

/** One row of the admin queue: an order plus what names its report. */
export interface ApiRegistryOrderQueueRow extends ApiRegistryOrder {
  /** The report's display codename. */
  report_label: string | null;
  /** The case family key. */
  report_family: string | null;
  /** The report's lifecycle stage. */
  report_stage: string | null;
}

/** `GET /registry-documents/orders` without `report_id`: a page of every
 * team's orders. */
export interface ApiRegistryOrderQueuePage {
  items: ApiRegistryOrderQueueRow[];
  total: number;
  version: string;
}

/** `GET /registry-documents/attention`: the open inbox items about
 * registry documents. */
export interface ApiRegistryAttention {
  documents_received: number;
  overdue: number;
  /** Desk replies nobody has read; absent on an API that predates it. */
  replies?: number;
  /** Emails from the desk that reached no order and that nobody has
   * attached or dismissed; absent on an API that predates it. */
  unmatched?: number;
  /** Why Armadillo's partner assignments are not kept in this environment
   * (a broken partner setting); null when they are, or the feature is off.
   * Not counted in `total`. Absent on an API that predates it. */
  partner_assignment_problem?: string | null;
  total: number;
}

/** What `GET /registry-documents/conversation` lists, oldest first. */
export type RegistryConversationItemKind =
  | 'order_sent'
  | 'message'
  | 'quote_recorded'
  | 'order_closed';

/** One entry of a report's Armadillo conversation. */
export interface ApiRegistryConversationItem {
  kind: string;
  at?: string | null;
  order_id: string;
  reference: string;
  company_name: string;
  /** `message` items only. */
  message?: ApiRegistryOrderMessage | null;
  /** `quote_recorded` items only. */
  quote?: ApiRegistryOrderQuote | null;
  /** `order_closed` items only: `cancelled` or `failed`. */
  status?: string | null;
  /** `order_closed` items only: why the order ended. */
  note?: string | null;
}

/** `GET /registry-documents/conversation?report_id=…`: every order a report
 * sent to Armadillo, and the mail on them as one conversation. */
export interface ApiRegistryOrderConversation {
  report_id: string;
  version: string;
  reply_enabled: boolean;
  /** Newest first. */
  orders: ApiRegistryOrder[];
  /** Oldest first. */
  items: ApiRegistryConversationItem[];
}

/** An order the API proposes for an email that reached none: the same
 * company name, or an order already in the email's thread. A proposal
 * only - a person attaches it, or picks another. */
export interface ApiRegistryInboundSuggestion {
  order_id: string;
  reference: string;
  company_name: string;
  country_code: string;
  status: RegistryOrderStatus;
  report_id: string;
  report_label: string | null;
  /** `company_name` or `thread`. */
  why: string;
}

/** One email the delivery worker recorded without finding its order
 * (`GET /registry-documents/inbound`). `sender`, `subject` and `excerpt`
 * are null for a row recorded before the worker kept them, and again once
 * the email was attached: the entry then lives on the order, and the row
 * also comes with no document names, countries or suggestions. */
export interface ApiRegistryInboundRow {
  id: string;
  received_at: string | null;
  /** The ledger status: `unmatched` or `needs_review` for a listed row. */
  status: string;
  /** The correlator's reason values. */
  reasons: string[] | null;
  sender: string | null;
  subject: string | null;
  /** The first 300 characters of the body excerpt. */
  excerpt: string | null;
  document_names: string[] | null;
  documents_count: number;
  links_count: number;
  /** A GTI reference the email named that resolved to no order. */
  order_reference: string | null;
  /** ISO-2 codes of the countries the email names. */
  countries_mentioned: string[] | null;
  suggestions: ApiRegistryInboundSuggestion[] | null;
  /** `attached` or `dismissed`; null while the email is still open. */
  resolution: string | null;
  resolved_at: string | null;
  resolved_by_user_id: string | null;
  resolution_note: string | null;
  /** The order it was attached to, once it was. */
  order_id: string | null;
  order_reference_attached: string | null;
  /** The report of the order it was attached to; null unless `order_id`
   * is set, and absent on an API that predates the field. */
  order_report_id?: string | null;
}

export interface ApiRegistryInboundPage {
  items: ApiRegistryInboundRow[];
  total: number;
  version: string;
}

/** The Registration Details table, as the documents state it. Every value
 * is as written in the document; a value the document does not give is
 * null, and the table shows it as "Not provided". */
export interface ApiRegistryDigestRegistration {
  entity_name: string | null;
  previous_names: string[];
  aliases: string[];
  entity_number: string | null;
  duns_number: string | null;
  date_of_incorporation: string | null;
  date_of_registration: string | null;
  status: string | null;
  registered_address: string | null;
  company_type: string | null;
  business_scope: string | null;
  /** The document pages the values came from. */
  source_pages: number[];
}

/** One officer: a row of the Management table, or a former director. */
export interface ApiRegistryDigestOfficer {
  name: string;
  positions: string[];
  appointed: string | null;
  resigned: string | null;
  current: boolean;
  source_pages: number[];
}

/** One row of the Ownership / Shareholders table. */
export interface ApiRegistryDigestShareholding {
  name: string;
  /** Registration or passport number, as written. */
  identifier: string | null;
  address: string | null;
  /** Percentage or share count, as written. */
  holding: string | null;
  /** Parsed only when the document states a percentage. */
  holding_percent: number | null;
  source_pages: number[];
}

/** One line of the sources appendix. */
export interface ApiRegistryDigestSource {
  registry: string | null;
  document_title: string | null;
  retrieved: string | null;
  source_pages: number[];
}

export interface ApiRegistryDigestCoverage {
  documents: ApiRegistryDigestDocumentCoverage[];
  model: string | null;
  chunks: number;
  /** Model calls that failed validation or raised; the run went on
   * without them, so part of the documents is not reflected. */
  chunks_failed: number;
  generated_at: string | null;
  /** Officer rows the model read outside an officer register (a filing
   * history line, a form's signatory) and the merge left out. Absent on
   * a digest that predates the gate. */
  officers_dropped?: number;
}

/** The audit opinion the accounts state. Mirrors the API's
 * `RegistryFinancials.audit_opinion`. */
export const REGISTRY_DIGEST_AUDIT_OPINIONS = [
  'unmodified',
  'qualified',
  'adverse',
  'disclaimer',
  'not_audited',
  'not_stated',
] as const;
export type RegistryDigestAuditOpinion = (typeof REGISTRY_DIGEST_AUDIT_OPINIONS)[number];

/** One period of the filed accounts. Every figure is as printed (brackets
 * and minus signs kept, never computed) and null when the accounts do not
 * give it; `unit` is as stated ("£m", "£000"). */
export interface ApiRegistryDigestFinancialPeriod {
  /** As written; ISO when unambiguous. */
  period_end: string | null;
  period_start: string | null;
  /** ISO 4217 when stated or unambiguous from the symbol. */
  currency: string | null;
  unit: string | null;
  /** True for the group's figures, false for the company's alone. */
  consolidated: boolean | null;
  revenue: string | null;
  gross_profit: string | null;
  operating_profit: string | null;
  profit_before_tax: string | null;
  profit_after_tax: string | null;
  total_assets: string | null;
  total_liabilities: string | null;
  /** Net assets, which is total equity. */
  net_assets: string | null;
  share_capital: string | null;
  cash: string | null;
  /** The average number of employees. */
  employees: string | null;
  source_pages: number[];
}

/** The Financial Information section, from the filed accounts. Mirrors
 * `RegistryFinancials`. */
export interface ApiRegistryDigestFinancials {
  /** "Financial Information". */
  title: string | null;
  /** The accounts' own titles, as printed. */
  document_titles: string[];
  /** As stated: group annual report, full, abridged, micro-entity ... */
  accounts_type: string | null;
  /** The three most recent period ends at most, latest first. */
  periods: ApiRegistryDigestFinancialPeriod[];
  auditor: string | null;
  audit_opinion: RegistryDigestAuditOpinion | null;
  /** One sentence, only when the accounts state a material uncertainty. */
  going_concern: string | null;
  caveats: string[];
}

/** One fact a document establishes about the company. */
export interface ApiRegistryDigestKeyFact {
  label: string;
  value: string;
  source_pages: number[];
}

/** What a document is and what it tells the reader, from its first
 * pages: the title, date and issuer as printed, the registry it came
 * from and a short summary. Mirrors `RegistryDocumentHead`. */
export interface ApiRegistryDigestDocumentHead {
  document_title?: string | null;
  issued?: string | null;
  issuer?: string | null;
  registry?: string | null;
  summary?: string | null;
}

/** The Corporate Information one register extract gives: the same
 * shapes as the digest's order-wide tables, for this file alone. */
export interface ApiRegistryDigestCorporate {
  registration?: ApiRegistryDigestRegistration | null;
  management?: ApiRegistryDigestOfficer[];
  former_management?: ApiRegistryDigestOfficer[];
  shareholders?: ApiRegistryDigestShareholding[];
}

/** How much of one document was read, as its own entry carries it. */
export interface ApiRegistryDigestEntryCoverage {
  pages_total?: number;
  pages_read?: number;
  pages_vision?: number;
  truncated?: boolean;
}

/** One delivered document's entry. Under schema version 2 only a
 * document that is neither corporate nor financial (a certificate of
 * good standing, articles of association) has one, with its key facts
 * and summary; under version 3 every read document does, keyed by the
 * file's hash, with a head, the body its role gives (the corporate
 * tables, the financials, or the key facts), its own sources and how
 * much of it was read. Mirrors `RegistryDocumentSection`. */
export interface ApiRegistryDigestDocumentSection {
  /** The catalogue's name for the product, else the code or the
   * filename's document part. */
  title: string | null;
  product_code: string | null;
  filename: string | null;
  /** The document's own title, as printed. */
  document_title: string | null;
  /** When it was issued, as written. */
  issued: string | null;
  issuer: string | null;
  key_facts: ApiRegistryDigestKeyFact[];
  /** Two to four sentences: what the document is and what it establishes. */
  summary: string | null;
  caveats: string[];
  /** The delivered file's hash: the stable key. Absent, like every field
   * below, on a version 2 entry. */
  sha256?: string | null;
  role?: RegistryDigestDocumentRole | null;
  head?: ApiRegistryDigestDocumentHead | null;
  corporate?: ApiRegistryDigestCorporate | null;
  financials?: ApiRegistryDigestFinancials | null;
  /** This file's sources alone. */
  sources?: ApiRegistryDigestSource[];
  coverage?: ApiRegistryDigestEntryCoverage | null;
}

/** The digest schema that read each document by its role (Financial
 * Information, document sections, notices). A digest stored under an
 * earlier version read every document as corporate. */
export const REGISTRY_DIGEST_ROLES_SCHEMA_VERSION = 2;

/** The digest schema that keeps every document's own entry, keyed by the
 * file's hash, with the order-wide tables derived from them. Mirrors the
 * API's `SCHEMA_VERSION`. */
export const REGISTRY_DIGEST_SCHEMA_VERSION = 3;

/** The stored result of a completed extraction: the Corporate Information
 * section's three tables, Financial Information, a section per other
 * document, and the appendix. Mirrors `RegistryDocumentDigest`. */
export interface ApiRegistryDocumentDigest {
  registration: ApiRegistryDigestRegistration;
  /** Current officers, in document order. */
  management: ApiRegistryDigestOfficer[];
  /** Former officers, alphabetical by name. */
  former_management: ApiRegistryDigestOfficer[];
  /** Largest holdings first, ties and unknowns alphabetical. */
  shareholders: ApiRegistryDigestShareholding[];
  /** Every document's sources, whatever it was read as. */
  sources: ApiRegistryDigestSource[];
  /** What the documents say they do not contain or qualify: every
   * section's, gathered (the financial and document sections carry their
   * own as well). */
  caveats: string[];
  coverage: ApiRegistryDigestCoverage;
  schema_version: number;
  /** Null when no accounts were read. Absent, like `documents` and
   * `notices`, on a digest that predates them (schema version 1). */
  financials?: ApiRegistryDigestFinancials | null;
  /** One section per other document, in delivery order. */
  documents?: ApiRegistryDigestDocumentSection[];
  /** One line per document that was not read: a trace report the desk
   * returned instead of a document. */
  notices?: string[];
}

/** `GET /registry-documents/orders/{id}/digest`: the state, and the digest
 * itself once a run has completed. */
export interface ApiRegistryDigestResponse {
  status: ApiRegistryDigestStatus;
  digest: ApiRegistryDocumentDigest | null;
}

/** `GET /registry-documents/orders/{id}/digest/docx`: a presigned link to
 * the Word file and the name to save it under. */
export interface ApiRegistryDigestDocx {
  url: string;
  filename: string;
}

/** `GET /registry-documents/orders/{id}/digest/markdown`: the digest as
 * report markdown (a "Registry documents" heading, one sub-heading per
 * document, pipe tables, every string escaped) and the run it renders.
 * 404 until a run has completed. */
export interface ApiRegistryDigestMarkdown {
  markdown: string;
  run?: string | null;
}

/** `POST /registry-documents/orders/{id}/digest/add-to-report`: the
 * insertion made, with the extraction's state as it now stands. */
export interface ApiRegistryDigestAddedToReportResponse {
  report_id: string;
  order_id: string;
  content_version: number;
  content_id: string;
  placement: string;
  approval_revoked?: boolean | null;
  digest: ApiRegistryDigestStatus;
}

/** The same route with `dry_run: true`: what the insertion would do,
 * nothing written. */
export interface ApiRegistryDigestAddToReportPreview {
  report_id: string;
  order_id: string;
  /** The version the insertion would build from. */
  content_version: number;
  placement: string;
  would_revoke?: boolean | null;
  /** The Word styles the report's template lacks, which the sections
   * approximate; empty for a Hub-written report. */
  style_fallbacks?: string[] | null;
}

/** Which orders the admin queue lists. Decided on the order row:
 * `outstanding` is open with documents still owed, `received` has at
 * least one document, `all` is every order. */
export type RegistryOrderQueueView = 'outstanding' | 'received' | 'all';

export interface RegistryOrderQueueQuery {
  view?: RegistryOrderQueueView;
  teamId?: string;
  /** Company name or GTI reference, case-insensitive substring. */
  q?: string;
  /** Only orders past their expected date with documents still owed. */
  late?: boolean;
  page?: number;
  pageSize?: number;
}

/** Which unmatched emails are listed: `open` is those nobody has attached
 * or dismissed yet, `all` includes the resolved ones. */
export type RegistryInboundView = 'open' | 'all';

export interface RegistryInboundQuery {
  view?: RegistryInboundView;
  page?: number;
  pageSize?: number;
}

/** `POST /registry-documents/orders`. Without `company_id` it is a
 * *manual* order for a company the search cannot find: the research desk
 * works from the name, jurisdiction, documents and registration number,
 * which the backend composes into the notes. */
export interface RegistryOrderCreateRequest {
  report_id: string;
  subject_id?: string | null;
  company_id?: string | null;
  company_name: string;
  /** ISO 3166-1 alpha-2. */
  country: string;
  products: string[];
  notes?: string | null;
  registration_number?: string | null;
  address?: string | null;
  town?: string | null;
  postcode?: string | null;
  /** Order even though an open order for the same documents is on the
   * report — a deliberate re-order. */
  allow_duplicate?: boolean;
}

/** One company's outcome in a batch of orders: the order it got, or why
 * it got none. */
export type RegistryOrderOutcome =
  | { ok: true; order: RegistryOrderDetail }
  | { ok: false; error: Error };

/** Places a batch of orders one by one, in the order given, and reports
 * each outcome through `onEach` as it lands. Resolves once every payload
 * has been tried and the list re-read. */
export type RegistryOrderBatchSubmit = (
  payloads: RegistryOrderCreateRequest[],
  onEach: (index: number, outcome: RegistryOrderOutcome) => void
) => Promise<void>;

/** `POST /registry-documents/orders/{id}/close`. Closing tells Armadillo
 * nothing: the order is cancelled or declined with the research desk
 * first, and closed here afterwards. */
export interface RegistryOrderCloseRequest {
  reason: RegistryOrderCloseReason;
  /** Free text; required when the reason is `other`. */
  note?: string | null;
}

/** The desk's quote as a reply records it. `price` is a decimal string
 * ("70.00"), as the API stores it. */
export interface RegistryOrderQuoteRequest {
  price: string;
  /** ISO 4217. */
  currency: string;
  /** Working days from now; sets the order's expected date. */
  turnaround_days: number;
}

/** `POST /registry-documents/orders/{id}/quote`: the desk's quote recorded
 * on its own, for one accepted or received by email outside the Hub.
 * Recording it gives the order its expected date; naming the desk's
 * message makes that message read as answered. */
export type RegistryOrderQuoteRecordRequest = RegistryOrderQuoteRequest & {
  /** The `messageId` of the desk message this quote answers; null for none. */
  answers_message_id?: string | null;
  /** Free text, at most `REGISTRY_ORDER_QUOTE_NOTE_MAX`. */
  note?: string | null;
};

/** `POST /registry-documents/orders/{id}/reply`. Sent as the shared
 * mailbox to the desk address that wrote, threaded on the message named,
 * and recorded on the order as an outbound message. */
export interface RegistryOrderReplyRequest {
  /** The `messageId` of the inbound message being answered. */
  in_reply_to: string;
  /** Plain text, required, at most `REGISTRY_ORDER_REPLY_TEXT_MAX`. */
  text: string;
  /** Record the desk's quote with the reply; only while the order has no
   * expected date yet. */
  quote?: RegistryOrderQuoteRequest | null;
}

export interface RegistryCompanySearchParams {
  name?: string;
  country?: string;
  duns?: string;
  registration_number?: string;
  page?: number;
  report_id?: string;
}

// ---------------------------------------------------------------------------
// Domain shapes
// ---------------------------------------------------------------------------

export interface RegistryCompanyCandidate {
  companyId: string;
  name: string;
  countryCode: string | null;
  address: string | null;
  registrationNumbers: string[];
  isBranch: boolean | null;
  isOutOfBusiness: boolean | null;
  sourceArm: string;
}

export interface RegistryCompanySearchResult {
  candidates: RegistryCompanyCandidate[];
  /** Total name matches upstream (25 per page); null when no name search ran. */
  matchedResults: number | null;
  page: number;
}

export interface RegistryProduct {
  code: string;
  description: string | null;
  availability: string | null;
  /** Numeric price, or null when the research team quotes it first. */
  price: number | null;
  currency: string | null;
  language: string | null;
  /** Expect a quote email before a document. */
  requiresQuote: boolean;
}

export interface RegistryOrder {
  id: string;
  reportId: string;
  subjectId: string | null;
  teamId: string;
  provider: string;
  companyId: string | null;
  /** A company the search could not find, ordered by name and notes. */
  manual: boolean;
  companyName: string;
  countryCode: string;
  products: string[];
  reference: string;
  notes: string | null;
  providerOrderId: string | null;
  status: RegistryOrderStatus;
  expectedDocumentCount: number;
  receivedDocumentCount: number;
  orderedByUserId: string | null;
  /** Null while only prepared; stamped when sent. */
  placedAt: string | null;
  completedAt: string | null;
  failureReason: string | null;
  /** Sum of the line prices when every line is priced in one currency. */
  listPrice: number | null;
  currency: string | null;
  /** `placedAt` plus the longest turnaround; null until placed and for a
   * quote order. */
  expectedAt: string | null;
  requiresQuote: boolean;
  placementAttempts: number;
  /** What the delivery worker concluded from the deliveries so far, when
   * it was not allowed to move `status` itself. */
  proposedStatus: RegistryOrderStatus | null;
  /** Emails from the desk that brought no new document (a quote, a
   * question, a cancellation confirmed) and that nobody has answered. */
  replyCount: number;
  lastReplyAt: string | null;
  /** Why a person closed the order; null unless it was closed. */
  closedReason: string | null;
  closedByUserId: string | null;
  /** The desk's quote, recorded with a reply or on its own; null until then. */
  quote: RegistryOrderQuote | null;
  /** The extraction of information from the delivered documents; null
   * until one was started. */
  digest: RegistryDigestStatus | null;
  /** The partner assignment that bills the order (Armadillo's automatic
   * assignment, or a manual one locked to Armadillo); null until one does. */
  backofficeAssignmentId: string | null;
  /** What the order adds to Armadillo's partner fee, in GBP; null while
   * the order is only prepared, and on an API that predates the field. */
  partnerFee: number | null;
  partnerFeeSource: RegistryFeeSource | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface RegistryOrderQuote {
  price: number | null;
  currency: string | null;
  turnaroundDays: number | null;
  recordedAt: string | null;
  recordedByUserId: string | null;
  /** The note left when the quote was recorded on its own; null otherwise. */
  note: string | null;
  /** The `messageId` of the desk message the quote answers; null when it
   * was recorded with a reply (the reply answers it) or against none. */
  answersMessageId: string | null;
}

export interface RegistryDeliveredFile {
  sha256: string;
  filename: string | null;
  kind: string;
  sizeBytes: number;
  contentType: string;
  productCode: string | null;
  reason: string | null;
  storedAt: string | null;
  /** Presigned GET, minted per request and short-lived. */
  downloadUrl: string | null;
}

export interface RegistryManualDetails {
  registrationNumber: string | null;
  address: string | null;
  town: string | null;
  postcode: string | null;
}

/** One email recorded on an order: from the desk (inbound), or a reply a
 * person sent from the order (outbound). */
export interface RegistryOrderMessage {
  /** When it was received, or for an outbound message when it was sent:
   * the one key the list sorts by. */
  receivedAt: string | null;
  sender: string | null;
  /** The stored body excerpt, or the reply as written. Sender-controlled
   * plain text: shown as text, never as markup, and its links are never
   * made clickable. */
  text: string;
  /** The email was longer than the excerpt kept. */
  truncated: boolean;
  /** How many files this email added; zero for a reply. */
  newDocuments: number;
  /** False when the strict rules refused the sender and the attachments
   * were withheld. Always true for an outbound message. */
  senderVerified: boolean;
  reasons: string[];
  /** The RFC Message-ID, what a reply names; null when none was recorded. */
  messageId: string | null;
  direction: RegistryOrderMessageDirection;
  /** Outbound only: who sent it. */
  sentByUserId: string | null;
  /** Outbound only: the `messageId` of the inbound message it answers. */
  inReplyTo: string | null;
  /** Inbound only: a reply names it, or any reply was sent after it. */
  answered: boolean;
  /** ISO-2 codes of the countries the email names; the order's own among
   * them or not. Empty for an outbound message. */
  countriesMentioned: string[];
  /** Who attached the email to the order by hand; null when it found the
   * order on its own. */
  attachedByUserId: string | null;
  /** How the email found its order; null for an outbound message and on
   * an API that predates the field. */
  matchedBy: RegistryOrderMessageMatch | null;
}

/** The report's document as the order's detail describes it. */
export interface RegistryReportContent {
  /** What the stored file is; null when the report has no content row. */
  kind: 'docx' | 'markdown' | null;
  version: number | null;
  lifecycleStage: string;
  approvalRequired: boolean;
  /** `approved` / `revoked` / `pending`; null where no approval is needed. */
  approvalStatus: string | null;
}

export interface RegistryOrderDetail extends RegistryOrder {
  catalogueSnapshot: RegistryProduct[];
  manualDetails: RegistryManualDetails | null;
  documents: RegistryDeliveredFile[];
  /** Every email recorded on the order, oldest first. */
  messages: RegistryOrderMessage[];
  /** The report's document; null on an API that predates "Add to report"
   * or when the order's report is missing. */
  reportContent: RegistryReportContent | null;
}

export interface RegistryOrderList {
  reportId: string;
  orders: RegistryOrder[];
  version: string;
}

export interface RegistryOrderQueueRow extends RegistryOrder {
  /** The report's codename; null for a report that has none. */
  reportLabel: string | null;
  reportFamily: string | null;
  reportStage: ReportStatus | null;
}

export interface RegistryOrderQueuePage {
  items: RegistryOrderQueueRow[];
  total: number;
  version: string;
}

/** Registry-document inbox items still open: what the Admin menu counts. */
export interface RegistryAttention {
  /** Deliveries nobody has looked at yet. */
  documentsReceived: number;
  /** Orders past their delivery window. */
  overdue: number;
  /** Reports where the research desk wrote without sending a document. */
  replies: number;
  /** Emails from the desk that reached no order and are still open. */
  unmatched: number;
  total: number;
  /** Why Armadillo's partner assignments are not kept here (a broken
   * partner setting): the Registry documents page's banner. Not a count. */
  partnerAssignmentProblem: string | null;
}

/** One entry of a report's Armadillo conversation: an order sent, an email
 * either way, a quote recorded or an order closed. */
export interface RegistryConversationItem {
  kind: RegistryConversationItemKind;
  /** When it happened; null for an email with no recorded time, which the
   * API sorts with its own order. */
  at: string | null;
  orderId: string;
  reference: string;
  companyName: string;
  /** `message` items only. */
  message: RegistryOrderMessage | null;
  /** `quote_recorded` items only. */
  quote: RegistryOrderQuote | null;
  /** `order_closed` items only. */
  status: 'cancelled' | 'failed' | null;
  /** `order_closed` items only: why the order ended. */
  note: string | null;
}

/** Every order a report sent to Armadillo and the mail on them, as the
 * report's Armadillo conversation. */
export interface RegistryOrderConversation {
  reportId: string;
  version: string;
  /** Replies can be sent from the Hub here (a send token and a mailbox). */
  replyEnabled: boolean;
  /** Newest first; prepared orders too, which add no item. */
  orders: RegistryOrder[];
  /** Oldest first. */
  items: RegistryConversationItem[];
}

export type RegistryInboundResolution = 'attached' | 'dismissed';

export interface RegistryInboundSuggestion {
  orderId: string;
  reference: string;
  companyName: string;
  countryCode: string;
  status: RegistryOrderStatus;
  reportId: string;
  reportLabel: string | null;
  /** `company_name` or `thread`; see `describeSuggestionWhy`. */
  why: string;
}

/** An email from the research desk that reached no order: what a person
 * needs to attach it to its order, or to dismiss it. */
export interface RegistryInboundRow {
  id: string;
  receivedAt: string | null;
  status: string;
  reasons: string[];
  /** Null, with `subject` and `excerpt`, for a row recorded before the
   * worker kept them (the email is still attachable), and for an attached
   * row, whose details moved onto the order. */
  sender: string | null;
  subject: string | null;
  /** The start of the body. Sender-controlled plain text: shown as text,
   * never as markup. */
  excerpt: string | null;
  documentNames: string[];
  documentsCount: number;
  linksCount: number;
  /** A GTI reference the email named that matched no order. */
  orderReference: string | null;
  countriesMentioned: string[];
  suggestions: RegistryInboundSuggestion[];
  resolution: RegistryInboundResolution | null;
  resolvedAt: string | null;
  resolvedByUserId: string | null;
  resolutionNote: string | null;
  orderId: string | null;
  orderReferenceAttached: string | null;
  /** The report of the order it was attached to, where the row links to;
   * null for a row on no order and on an API that predates the field. */
  orderReportId: string | null;
}

export interface RegistryInboundPage {
  items: RegistryInboundRow[];
  total: number;
  version: string;
}

export interface RegistryDigestDocumentCoverage {
  filename: string | null;
  productCode: string | null;
  pagesTotal: number;
  pagesRead: number;
  /** Pages without a text layer, read as images. */
  pagesVision: number;
  /** Pages past the caps were dropped: the tables may be incomplete. */
  truncated: boolean;
  /** What the document was read as; null on a digest that predates roles,
   * whose documents were all read as corporate. */
  role: RegistryDigestDocumentRole | null;
  /** The section title of a financial or other document. */
  title: string | null;
}

/** The state of the extraction on an order: what the button, the status
 * line and the "Run again" action key off. */
export interface RegistryDigestStatus {
  status: RegistryDigestRunStatus;
  requestedAt: string | null;
  requestedByUserId: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  /** Failed only: a short error, never document text. */
  error: string | null;
  model: string | null;
  documents: RegistryDigestDocumentCoverage[];
  chunks: number;
  chunksFailed: number;
  /** The digest's last insertion into the report; null until the first
   * add. */
  addedToReport: RegistryDigestAddedToReportMark | null;
}

/** The record of the digest's last insertion into the report. */
export interface RegistryDigestAddedToReportMark {
  /** The report content version the insertion created. */
  version: number;
  at: string | null;
  byUserId: string | null;
  /** Null on a placement this build does not know. */
  placement: RegistryDigestPlacement | null;
  /** The block in the report is up to date: still carried by the latest
   * content version, and from the run served now. False with `present`
   * true once a later run completed (the block is out of date and an
   * update replaces it). */
  current: boolean;
  /** The latest content version still carries this order's block; false
   * once a later version dropped it (a regeneration, a rewrite through
   * feedback), when the block must be added again. */
  present: boolean;
  /** The content version `present` was last decided against: the version
   * added, on an API that predates the check. */
  checkedVersion: number;
}

export interface RegistryDigestRegistration {
  entityName: string | null;
  previousNames: string[];
  aliases: string[];
  entityNumber: string | null;
  dunsNumber: string | null;
  dateOfIncorporation: string | null;
  dateOfRegistration: string | null;
  status: string | null;
  registeredAddress: string | null;
  companyType: string | null;
  businessScope: string | null;
  sourcePages: number[];
}

export interface RegistryDigestOfficer {
  name: string;
  positions: string[];
  appointed: string | null;
  resigned: string | null;
  current: boolean;
  sourcePages: number[];
}

export interface RegistryDigestShareholding {
  name: string;
  identifier: string | null;
  address: string | null;
  holding: string | null;
  holdingPercent: number | null;
  sourcePages: number[];
}

export interface RegistryDigestSource {
  registry: string | null;
  documentTitle: string | null;
  retrieved: string | null;
  sourcePages: number[];
}

export interface RegistryDigestCoverage {
  documents: RegistryDigestDocumentCoverage[];
  model: string | null;
  chunks: number;
  /** Model calls that produced nothing; part of the documents is not
   * reflected in the tables when this is above zero. */
  chunksFailed: number;
  generatedAt: string | null;
  /** Officer rows read outside an officer register and left out; zero on
   * a digest that predates the gate. */
  officersDropped: number;
}

/** The figures of one accounting period, as printed; null where the
 * accounts do not give one. */
export interface RegistryDigestFinancialFigures {
  revenue: string | null;
  grossProfit: string | null;
  operatingProfit: string | null;
  profitBeforeTax: string | null;
  profitAfterTax: string | null;
  totalAssets: string | null;
  totalLiabilities: string | null;
  netAssets: string | null;
  shareCapital: string | null;
  cash: string | null;
  employees: string | null;
}

/** One figure of a period: a row of the Financial Information table. */
export type RegistryDigestFinancialMetric = keyof RegistryDigestFinancialFigures;

export interface RegistryDigestFinancialPeriod extends RegistryDigestFinancialFigures {
  periodEnd: string | null;
  periodStart: string | null;
  currency: string | null;
  unit: string | null;
  /** True for the group's figures, false for the company's alone, null
   * when the accounts do not say. */
  consolidated: boolean | null;
  sourcePages: number[];
}

export interface RegistryDigestFinancials {
  title: string;
  documentTitles: string[];
  accountsType: string | null;
  /** Latest first, three at most. */
  periods: RegistryDigestFinancialPeriod[];
  auditor: string | null;
  auditOpinion: RegistryDigestAuditOpinion | null;
  goingConcern: string | null;
  caveats: string[];
}

export interface RegistryDigestKeyFact {
  label: string;
  value: string;
  sourcePages: number[];
}

/** What a document is and what it tells the reader, from its first pages. */
export interface RegistryDigestDocumentHead {
  documentTitle: string | null;
  issued: string | null;
  issuer: string | null;
  registry: string | null;
  summary: string | null;
}

/** The Corporate Information one register extract gives: the same
 * shapes as the order-wide tables, for this file alone. */
export interface RegistryDigestCorporate {
  registration: RegistryDigestRegistration;
  management: RegistryDigestOfficer[];
  formerManagement: RegistryDigestOfficer[];
  shareholders: RegistryDigestShareholding[];
}

/** One delivered document's entry. A version 2 digest has one only for
 * a document that is neither corporate nor financial, with the key facts
 * and summary alone; a version 3 digest has one for every read document,
 * keyed by `sha256`, with the head, the body its role gives, its own
 * sources and how much of it was read. */
export interface RegistryDigestDocumentSection {
  title: string;
  productCode: string | null;
  filename: string | null;
  documentTitle: string | null;
  issued: string | null;
  issuer: string | null;
  keyFacts: RegistryDigestKeyFact[];
  summary: string | null;
  caveats: string[];
  /** The delivered file's hash: the stable key. Null, like every field
   * below, on a version 2 entry. */
  sha256: string | null;
  /** What the document was read as; null on a version 2 entry, whose
   * body is the key facts. */
  role: RegistryDigestDocumentRole | null;
  head: RegistryDigestDocumentHead | null;
  /** The register extract's tables; null unless read as corporate. */
  corporate: RegistryDigestCorporate | null;
  /** The filed accounts' figures; null unless read as financial. */
  financials: RegistryDigestFinancials | null;
  /** This file's sources alone; empty on a version 2 entry. */
  sources: RegistryDigestSource[];
  /** How much of this file was read, with the file named; null on a
   * version 2 entry. */
  coverage: RegistryDigestDocumentCoverage | null;
}

/** A completed extraction: one entry per read document (schema version
 * 3), with the order-wide Corporate Information tables, Financial
 * Information, caveats and sources derived from them; the notices for
 * documents not read; and how much was read. A version 2 digest keeps
 * the order-wide tables as stored, with entries for the documents that
 * are neither corporate nor financial alone; a version 1 digest reads as
 * no financials, no entries and no notices. */
export interface RegistryDocumentDigest {
  registration: RegistryDigestRegistration;
  /** Current officers, in document order. */
  management: RegistryDigestOfficer[];
  /** Former officers, alphabetical by name. */
  formerManagement: RegistryDigestOfficer[];
  /** Largest holdings first, ties and unknowns alphabetical. */
  shareholders: RegistryDigestShareholding[];
  sources: RegistryDigestSource[];
  caveats: string[];
  coverage: RegistryDigestCoverage;
  schemaVersion: number;
  financials: RegistryDigestFinancials | null;
  /** In delivery order. */
  documents: RegistryDigestDocumentSection[];
  notices: string[];
}

/** The extraction's state with, once complete, the digest itself. */
export interface RegistryDigestResult {
  status: RegistryDigestStatus;
  digest: RegistryDocumentDigest | null;
}

/** The digest as report markdown, with the run it renders. */
export interface RegistryDigestMarkdown {
  markdown: string;
  run: string | null;
}

/** The insertion of the digest into the report, as made. */
export interface RegistryDigestAddedToReport {
  reportId: string;
  orderId: string;
  contentVersion: number;
  contentId: string;
  placement: RegistryDigestPlacement | null;
  approvalRevoked: boolean;
  /** The extraction's state as it now stands, `addedToReport` set. */
  digest: RegistryDigestStatus;
}

/** What the insertion would do, from a dry run: nothing is written. */
export interface RegistryDigestAddToReportPreview {
  reportId: string;
  orderId: string;
  /** The version the insertion would build from; the write names it so a
   * report that moved on meanwhile is refused instead of overwritten. */
  contentVersion: number;
  placement: RegistryDigestPlacement | null;
  /** The report is approved: adding withdraws the approval. */
  wouldRevoke: boolean;
  /** The Word styles the report's template lacks; empty for a Hub-written
   * report. */
  styleFallbacks: string[];
}

export interface RegistryDigestDocx {
  url: string;
  filename: string;
}

// ---------------------------------------------------------------------------
// Transforms
// ---------------------------------------------------------------------------

/** A Decimal off the wire (a JSON number or a string) as a number; null for
 * none and for anything that is not one. */
export function toNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

export function transformToRegistryCompanyCandidate(
  api: ApiRegistryCompanyCandidate
): RegistryCompanyCandidate {
  return {
    companyId: api.company_id,
    name: api.name,
    countryCode: api.country_code ?? null,
    address: api.address ?? null,
    registrationNumbers: api.registration_numbers ?? [],
    isBranch: api.is_branch ?? null,
    isOutOfBusiness: api.is_out_of_business ?? null,
    sourceArm: api.source_arm,
  };
}

export function transformToRegistryCompanySearchResult(
  api: ApiRegistryCompanySearchResponse
): RegistryCompanySearchResult {
  return {
    candidates: (api.candidates ?? []).map(transformToRegistryCompanyCandidate),
    matchedResults: api.matched_results ?? null,
    page: api.page ?? 1,
  };
}

export function transformToRegistryProduct(
  api: ApiRegistryProduct | ApiRegistryCatalogueLine
): RegistryProduct {
  return {
    code: api.code,
    description: api.description ?? null,
    availability: api.availability ?? null,
    price: toNumber(api.price),
    currency: api.currency ?? null,
    language: 'language' in api ? (api.language ?? null) : null,
    requiresQuote: Boolean(api.requires_quote),
  };
}

export function transformToRegistryOrder(api: ApiRegistryOrder): RegistryOrder {
  return {
    id: api.id,
    reportId: api.report_id,
    subjectId: api.subject_id ?? null,
    teamId: api.team_id,
    provider: api.provider,
    companyId: api.company_id ?? null,
    manual: Boolean(api.manual ?? api.company_id == null),
    companyName: api.company_name,
    countryCode: api.country_code,
    products: api.products ?? [],
    reference: api.reference,
    notes: api.notes ?? null,
    providerOrderId: api.provider_order_id ?? null,
    status: api.status,
    expectedDocumentCount: api.expected_document_count ?? 0,
    receivedDocumentCount: api.received_document_count ?? 0,
    orderedByUserId: api.ordered_by_user_id ?? null,
    placedAt: api.placed_at ?? null,
    completedAt: api.completed_at ?? null,
    failureReason: api.failure_reason ?? null,
    listPrice: toNumber(api.list_price),
    currency: api.currency ?? null,
    expectedAt: api.expected_at ?? null,
    requiresQuote: Boolean(api.requires_quote),
    placementAttempts: api.placement_attempts ?? 0,
    proposedStatus: api.proposed_status ?? null,
    replyCount: api.reply_count ?? 0,
    lastReplyAt: api.last_reply_at ?? null,
    closedReason: api.closed_reason ?? null,
    closedByUserId: api.closed_by_user_id ?? null,
    quote: transformToRegistryOrderQuote(api.quote),
    digest: registryDigestStatusOf(api.digest),
    ...partnerFeeFieldsOf(api),
    createdAt: api.created_at ?? null,
    updatedAt: api.updated_at ?? null,
  };
}

const FEE_SOURCES: ReadonlySet<string> = new Set([
  'list_price',
  'quote',
  'awaiting_quote',
  'not_gbp',
  'not_billable',
]);

/** The fee source the wire names, or null for none and for a value this
 * build does not know. */
export function registryFeeSourceOf(value: unknown): RegistryFeeSource | null {
  return typeof value === 'string' && FEE_SOURCES.has(value) ? (value as RegistryFeeSource) : null;
}

/** An order's share of Armadillo's partner fee, as the API worked it out. */
function partnerFeeFieldsOf(
  api: ApiRegistryOrder
): Pick<RegistryOrder, 'backofficeAssignmentId' | 'partnerFee' | 'partnerFeeSource'> {
  return {
    backofficeAssignmentId: api.backoffice_assignment_id ?? null,
    partnerFee: toNumber(api.partner_fee),
    partnerFeeSource: registryFeeSourceOf(api.partner_fee_source),
  };
}

/** The recorded quote, or null for none: an API that predates replying,
 * an order nobody has quoted, or a body in another shape. */
export function transformToRegistryOrderQuote(
  api: ApiRegistryOrderQuote | null | undefined
): RegistryOrderQuote | null {
  if (!api || typeof api !== 'object') {
    return null;
  }
  const days = api.turnaround_days;
  return {
    price: toNumber(api.price),
    currency: api.currency ?? null,
    turnaroundDays: typeof days === 'number' && Number.isFinite(days) ? days : null,
    recordedAt: api.recorded_at ?? null,
    recordedByUserId: api.recorded_by_user_id ?? null,
    note: api.note ?? null,
    answersMessageId: api.answers_message_id ?? null,
  };
}

/** How many files an email added: the API sends the count, and a list of
 * the files is counted rather than refused. */
function countNewDocuments(value: ApiRegistryOrderMessage['new_documents']): number {
  if (Array.isArray(value)) {
    return value.length;
  }
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : 0;
}

export function transformToRegistryOrderMessage(
  api: ApiRegistryOrderMessage
): RegistryOrderMessage {
  // Every message was inbound before replying existed, so only an explicit
  // outbound reads as one.
  const outbound = api.direction === 'outbound';
  return {
    // An outbound message is placed by when it was sent.
    receivedAt: api.received_at ?? api.sent_at ?? null,
    sender: api.sender ?? null,
    text: typeof api.text === 'string' ? api.text : '',
    truncated: Boolean(api.truncated),
    newDocuments: countNewDocuments(api.new_documents),
    // Only an explicit refusal marks a sender: a body without the flag
    // says nothing either way. A reply this side sent is never suspect.
    senderVerified: outbound || api.sender_verified !== false,
    reasons: api.reasons ?? [],
    messageId: typeof api.message_id === 'string' && api.message_id ? api.message_id : null,
    direction: outbound ? 'outbound' : 'inbound',
    sentByUserId: outbound ? (api.sent_by_user_id ?? null) : null,
    inReplyTo: outbound ? (api.in_reply_to ?? null) : null,
    answered: !outbound && api.answered === true,
    countriesMentioned: outbound ? [] : stringList(api.countries_mentioned),
    attachedByUserId: outbound ? null : (api.attached_by_user_id ?? null),
    matchedBy: outbound ? null : messageMatchOf(api.matched_by),
  };
}

const MESSAGE_MATCHES: ReadonlySet<string> = new Set(['reference', 'thread', 'manual']);

/** The match the wire names, or null for none and for a value this build
 * does not know. */
function messageMatchOf(value: unknown): RegistryOrderMessageMatch | null {
  return typeof value === 'string' && MESSAGE_MATCHES.has(value)
    ? (value as RegistryOrderMessageMatch)
    : null;
}

/** A list of strings off the wire, or none: a missing field and a field in
 * another shape both read as empty. */
function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : [];
}

export function transformToRegistryOrderDetail(api: ApiRegistryOrderDetail): RegistryOrderDetail {
  const details = api.manual_details;
  return {
    ...transformToRegistryOrder(api),
    catalogueSnapshot: (api.catalogue_snapshot ?? []).map(transformToRegistryProduct),
    manualDetails: details
      ? {
          registrationNumber: details.registration_number ?? null,
          address: details.address ?? null,
          town: details.town ?? null,
          postcode: details.postcode ?? null,
        }
      : null,
    documents: (api.documents ?? []).map((file) => ({
      sha256: file.sha256,
      filename: file.filename ?? null,
      kind: file.kind,
      sizeBytes: file.size_bytes ?? 0,
      contentType: file.content_type,
      productCode: file.product_code ?? null,
      reason: file.reason ?? null,
      storedAt: file.stored_at ?? null,
      downloadUrl: file.download_url ?? null,
    })),
    messages: (api.messages ?? []).map(transformToRegistryOrderMessage),
    reportContent: registryReportContentOf(api.report_content),
  };
}

/** The report's document off the detail, or null for none: an API that
 * predates it, a missing report, a body in another shape. */
export function registryReportContentOf(value: unknown): RegistryReportContent | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const api = value as ApiRegistryReportContent;
  if (typeof api.lifecycle_stage !== 'string') {
    return null;
  }
  return {
    kind: api.kind === 'docx' || api.kind === 'markdown' ? api.kind : null,
    version: typeof api.version === 'number' ? api.version : null,
    lifecycleStage: api.lifecycle_stage,
    approvalRequired: Boolean(api.approval_required),
    approvalStatus: api.approval_status ?? null,
  };
}

export function transformToRegistryOrderList(api: ApiRegistryOrderList): RegistryOrderList {
  return {
    reportId: api.report_id,
    orders: (api.orders ?? []).map(transformToRegistryOrder),
    version: api.version,
  };
}

export function transformToRegistryOrderQueueRow(
  api: ApiRegistryOrderQueueRow
): RegistryOrderQueueRow {
  return {
    ...transformToRegistryOrder(api),
    reportLabel: api.report_label ?? null,
    reportFamily: api.report_family ?? null,
    reportStage: (api.report_stage as ReportStatus | null | undefined) ?? null,
  };
}

export function transformToRegistryOrderQueuePage(
  api: ApiRegistryOrderQueuePage
): RegistryOrderQueuePage {
  return {
    items: (api.items ?? []).map(transformToRegistryOrderQueueRow),
    total: api.total ?? 0,
    version: api.version ?? '',
  };
}

export function transformToRegistryAttention(api: ApiRegistryAttention): RegistryAttention {
  const documentsReceived = api.documents_received ?? 0;
  const overdue = api.overdue ?? 0;
  const replies = api.replies ?? 0;
  const unmatched = api.unmatched ?? 0;
  return {
    documentsReceived,
    overdue,
    replies,
    unmatched,
    total: api.total ?? documentsReceived + overdue + replies + unmatched,
    partnerAssignmentProblem: api.partner_assignment_problem || null,
  };
}

const CONVERSATION_KINDS: ReadonlySet<string> = new Set([
  'order_sent',
  'message',
  'quote_recorded',
  'order_closed',
]);

/** A closed order's status as the conversation names it; null otherwise. */
function closedStatusOf(value: unknown): 'cancelled' | 'failed' | null {
  return value === 'cancelled' || value === 'failed' ? value : null;
}

/** One conversation entry, or null for an entry this build cannot show: a
 * kind it does not know, or an email item that carries no email. */
export function transformToRegistryConversationItem(
  api: ApiRegistryConversationItem
): RegistryConversationItem | null {
  if (!CONVERSATION_KINDS.has(api.kind)) {
    return null;
  }
  const message = api.message ? transformToRegistryOrderMessage(api.message) : null;
  if (api.kind === 'message' && message === null) {
    return null;
  }
  return {
    kind: api.kind as RegistryConversationItemKind,
    at: api.at ?? null,
    orderId: api.order_id,
    reference: api.reference,
    companyName: api.company_name,
    message,
    quote: transformToRegistryOrderQuote(api.quote),
    status: closedStatusOf(api.status),
    note: api.note ?? null,
  };
}

export function transformToRegistryConversation(
  api: ApiRegistryOrderConversation
): RegistryOrderConversation {
  return {
    reportId: api.report_id,
    version: api.version ?? '',
    replyEnabled: api.reply_enabled === true,
    orders: (api.orders ?? []).map(transformToRegistryOrder),
    items: (api.items ?? [])
      .map(transformToRegistryConversationItem)
      .filter((item): item is RegistryConversationItem => item !== null),
  };
}

export function transformToRegistryInboundSuggestion(
  api: ApiRegistryInboundSuggestion
): RegistryInboundSuggestion {
  return {
    orderId: api.order_id,
    reference: api.reference,
    companyName: api.company_name,
    countryCode: api.country_code,
    status: api.status,
    reportId: api.report_id,
    reportLabel: api.report_label ?? null,
    why: api.why,
  };
}

/** The resolution the wire names, or null for an open row and for a value
 * this build does not know. */
function inboundResolutionOf(value: unknown): RegistryInboundResolution | null {
  return value === 'attached' || value === 'dismissed' ? value : null;
}

export function transformToRegistryInboundRow(api: ApiRegistryInboundRow): RegistryInboundRow {
  return {
    id: api.id,
    receivedAt: api.received_at ?? null,
    status: api.status,
    reasons: stringList(api.reasons),
    sender: api.sender ?? null,
    subject: api.subject ?? null,
    excerpt: api.excerpt ?? null,
    documentNames: stringList(api.document_names),
    documentsCount: api.documents_count ?? 0,
    linksCount: api.links_count ?? 0,
    orderReference: api.order_reference ?? null,
    countriesMentioned: stringList(api.countries_mentioned),
    suggestions: (api.suggestions ?? []).map(transformToRegistryInboundSuggestion),
    resolution: inboundResolutionOf(api.resolution),
    resolvedAt: api.resolved_at ?? null,
    resolvedByUserId: api.resolved_by_user_id ?? null,
    resolutionNote: api.resolution_note ?? null,
    orderId: api.order_id ?? null,
    orderReferenceAttached: api.order_reference_attached ?? null,
    orderReportId: api.order_report_id ?? null,
  };
}

export function transformToRegistryInboundPage(api: ApiRegistryInboundPage): RegistryInboundPage {
  return {
    items: (api.items ?? []).map(transformToRegistryInboundRow),
    total: api.total ?? 0,
    version: api.version ?? '',
  };
}

/** A count off the wire, or the fallback for a missing or malformed one. */
function countOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** Page numbers off the wire; anything that is not a number is dropped. */
function pageList(value: unknown): number[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is number => typeof entry === 'number' && Number.isFinite(entry))
    : [];
}

const DIGEST_DOCUMENT_ROLES: ReadonlySet<string> = new Set(REGISTRY_DIGEST_DOCUMENT_ROLES);

/** A document's role off the wire; one this build does not know reads as
 * none, as a digest that predates roles does. */
function digestRoleOf(value: unknown): RegistryDigestDocumentRole | null {
  return typeof value === 'string' && DIGEST_DOCUMENT_ROLES.has(value)
    ? (value as RegistryDigestDocumentRole)
    : null;
}

export function transformToRegistryDigestDocumentCoverage(
  api: ApiRegistryDigestDocumentCoverage
): RegistryDigestDocumentCoverage {
  return {
    filename: api.filename ?? null,
    productCode: api.product_code ?? null,
    pagesTotal: countOr(api.pages_total, 0),
    pagesRead: countOr(api.pages_read, 0),
    pagesVision: countOr(api.pages_vision, 0),
    truncated: Boolean(api.truncated),
    role: digestRoleOf(api.role),
    title: api.title ?? null,
  };
}

export function transformToRegistryDigestStatus(
  api: ApiRegistryDigestStatus
): RegistryDigestStatus {
  return {
    status: api.status,
    requestedAt: api.requested_at ?? null,
    requestedByUserId: api.requested_by_user_id ?? null,
    startedAt: api.started_at ?? null,
    finishedAt: api.finished_at ?? null,
    error: api.error ?? null,
    model: api.model ?? null,
    documents: (api.documents ?? []).map(transformToRegistryDigestDocumentCoverage),
    chunks: countOr(api.chunks, 0),
    chunksFailed: countOr(api.chunks_failed, 0),
    addedToReport: registryDigestAddedToReportMarkOf(api.added_to_report),
  };
}

const DIGEST_PLACEMENTS: ReadonlySet<string> = new Set([
  'replaced_block',
  'inside_section',
  'before_appendix',
  'before_footer',
  'end',
]);

/** A placement off the wire, or null for one this build does not know. */
export function registryDigestPlacementOf(value: unknown): RegistryDigestPlacement | null {
  return typeof value === 'string' && DIGEST_PLACEMENTS.has(value)
    ? (value as RegistryDigestPlacement)
    : null;
}

/** The insertion record off the extraction's state, or null for none: an
 * API that predates it, an order never added, a body in another shape (a
 * record without its version says nothing a line could show). */
export function registryDigestAddedToReportMarkOf(
  value: unknown
): RegistryDigestAddedToReportMark | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const api = value as ApiRegistryDigestAddedToReport;
  if (typeof api.version !== 'number') {
    return null;
  }
  return {
    version: api.version,
    at: api.at ?? null,
    byUserId: api.by_user_id ?? null,
    placement: registryDigestPlacementOf(api.placement),
    current: Boolean(api.current),
    // An API that predates the check never dropped a block, and checked
    // nothing past the version it added.
    present: api.present === undefined || api.present === null ? true : Boolean(api.present),
    checkedVersion: typeof api.checked_version === 'number' ? api.checked_version : api.version,
  };
}

export function transformToRegistryDigestAddedToReport(
  api: ApiRegistryDigestAddedToReportResponse
): RegistryDigestAddedToReport {
  return {
    reportId: api.report_id,
    orderId: api.order_id,
    contentVersion: api.content_version,
    contentId: api.content_id,
    placement: registryDigestPlacementOf(api.placement),
    approvalRevoked: Boolean(api.approval_revoked),
    digest: transformToRegistryDigestStatus(api.digest),
  };
}

export function transformToRegistryDigestAddToReportPreview(
  api: ApiRegistryDigestAddToReportPreview
): RegistryDigestAddToReportPreview {
  return {
    reportId: api.report_id,
    orderId: api.order_id,
    contentVersion: api.content_version,
    placement: registryDigestPlacementOf(api.placement),
    wouldRevoke: Boolean(api.would_revoke),
    styleFallbacks: stringList(api.style_fallbacks),
  };
}

export function transformToRegistryDigestMarkdown(
  api: ApiRegistryDigestMarkdown
): RegistryDigestMarkdown {
  return {
    markdown: typeof api.markdown === 'string' ? api.markdown : '',
    run: typeof api.run === 'string' && api.run ? api.run : null,
  };
}

const DIGEST_RUN_STATUSES: ReadonlySet<string> = new Set(['running', 'completed', 'failed']);

/** The extraction's state off an order row, or null for none: an API that
 * predates it, an order nobody has digested, a body in another shape, or
 * a status this build does not know (which must not read as a run to
 * poll or to show). */
export function registryDigestStatusOf(value: unknown): RegistryDigestStatus | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const api = value as ApiRegistryDigestStatus;
  return DIGEST_RUN_STATUSES.has(api.status) ? transformToRegistryDigestStatus(api) : null;
}

export function transformToRegistryDigestRegistration(
  api: ApiRegistryDigestRegistration
): RegistryDigestRegistration {
  return {
    entityName: api.entity_name ?? null,
    previousNames: stringList(api.previous_names),
    aliases: stringList(api.aliases),
    entityNumber: api.entity_number ?? null,
    dunsNumber: api.duns_number ?? null,
    dateOfIncorporation: api.date_of_incorporation ?? null,
    dateOfRegistration: api.date_of_registration ?? null,
    status: api.status ?? null,
    registeredAddress: api.registered_address ?? null,
    companyType: api.company_type ?? null,
    businessScope: api.business_scope ?? null,
    sourcePages: pageList(api.source_pages),
  };
}

export function transformToRegistryDigestOfficer(
  api: ApiRegistryDigestOfficer
): RegistryDigestOfficer {
  return {
    name: api.name,
    positions: stringList(api.positions),
    appointed: api.appointed ?? null,
    resigned: api.resigned ?? null,
    current: Boolean(api.current),
    sourcePages: pageList(api.source_pages),
  };
}

export function transformToRegistryDigestShareholding(
  api: ApiRegistryDigestShareholding
): RegistryDigestShareholding {
  const percent = api.holding_percent;
  return {
    name: api.name,
    identifier: api.identifier ?? null,
    address: api.address ?? null,
    holding: api.holding ?? null,
    holdingPercent: typeof percent === 'number' && Number.isFinite(percent) ? percent : null,
    sourcePages: pageList(api.source_pages),
  };
}

export function transformToRegistryDigestSource(
  api: ApiRegistryDigestSource
): RegistryDigestSource {
  return {
    registry: api.registry ?? null,
    documentTitle: api.document_title ?? null,
    retrieved: api.retrieved ?? null,
    sourcePages: pageList(api.source_pages),
  };
}

/** An empty registration table: every row reads "Not provided". Used when
 * the API's digest carries none, so the view never has to guard it. */
function emptyRegistration(): ApiRegistryDigestRegistration {
  return {
    entity_name: null,
    previous_names: [],
    aliases: [],
    entity_number: null,
    duns_number: null,
    date_of_incorporation: null,
    date_of_registration: null,
    status: null,
    registered_address: null,
    company_type: null,
    business_scope: null,
    source_pages: [],
  };
}

/** A figure as printed: the text, or null for none. A number (an
 * employee count sent as one) keeps its digits; anything else is none. */
function figureOf(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function transformToRegistryDigestFinancialPeriod(
  api: ApiRegistryDigestFinancialPeriod
): RegistryDigestFinancialPeriod {
  return {
    periodEnd: api.period_end ?? null,
    periodStart: api.period_start ?? null,
    currency: api.currency ?? null,
    unit: api.unit ?? null,
    consolidated: typeof api.consolidated === 'boolean' ? api.consolidated : null,
    revenue: figureOf(api.revenue),
    grossProfit: figureOf(api.gross_profit),
    operatingProfit: figureOf(api.operating_profit),
    profitBeforeTax: figureOf(api.profit_before_tax),
    profitAfterTax: figureOf(api.profit_after_tax),
    totalAssets: figureOf(api.total_assets),
    totalLiabilities: figureOf(api.total_liabilities),
    netAssets: figureOf(api.net_assets),
    shareCapital: figureOf(api.share_capital),
    cash: figureOf(api.cash),
    employees: figureOf(api.employees),
    sourcePages: pageList(api.source_pages),
  };
}

const AUDIT_OPINIONS: ReadonlySet<string> = new Set(REGISTRY_DIGEST_AUDIT_OPINIONS);

/** The section's title when the API gives none. */
export const REGISTRY_DIGEST_FINANCIALS_TITLE = 'Financial Information';

export function transformToRegistryDigestFinancials(
  api: ApiRegistryDigestFinancials
): RegistryDigestFinancials {
  const opinion = api.audit_opinion;
  return {
    title: api.title?.trim() || REGISTRY_DIGEST_FINANCIALS_TITLE,
    documentTitles: stringList(api.document_titles),
    accountsType: api.accounts_type ?? null,
    periods: (api.periods ?? []).map(transformToRegistryDigestFinancialPeriod),
    auditor: api.auditor ?? null,
    // An opinion this build does not know reads as none rather than as a
    // label it cannot word.
    auditOpinion:
      typeof opinion === 'string' && AUDIT_OPINIONS.has(opinion)
        ? (opinion as RegistryDigestAuditOpinion)
        : null,
    goingConcern: api.going_concern ?? null,
    caveats: stringList(api.caveats),
  };
}

function transformToRegistryDigestDocumentHead(
  api: ApiRegistryDigestDocumentHead | null | undefined
): RegistryDigestDocumentHead | null {
  if (!api || typeof api !== 'object') {
    return null;
  }
  return {
    documentTitle: api.document_title ?? null,
    issued: api.issued ?? null,
    issuer: api.issuer ?? null,
    registry: api.registry ?? null,
    summary: api.summary ?? null,
  };
}

function transformToRegistryDigestCorporate(
  api: ApiRegistryDigestCorporate | null | undefined
): RegistryDigestCorporate | null {
  if (!api || typeof api !== 'object') {
    return null;
  }
  return {
    registration: transformToRegistryDigestRegistration(api.registration ?? emptyRegistration()),
    management: (api.management ?? []).map(transformToRegistryDigestOfficer),
    formerManagement: (api.former_management ?? []).map(transformToRegistryDigestOfficer),
    shareholders: (api.shareholders ?? []).map(transformToRegistryDigestShareholding),
  };
}

/** An entry's own coverage as a document coverage line, the file named
 * from the entry: what the "Read n of m pages" sentence reads. */
function transformToRegistryDigestEntryCoverage(
  api: ApiRegistryDigestDocumentSection
): RegistryDigestDocumentCoverage | null {
  const coverage = api.coverage;
  if (!coverage || typeof coverage !== 'object') {
    return null;
  }
  return transformToRegistryDigestDocumentCoverage({
    filename: api.filename ?? null,
    product_code: api.product_code ?? null,
    pages_total: countOr(coverage.pages_total, 0),
    pages_read: countOr(coverage.pages_read, 0),
    pages_vision: countOr(coverage.pages_vision, 0),
    truncated: Boolean(coverage.truncated),
    role: api.role,
    title: api.title,
  });
}

export function transformToRegistryDigestDocumentSection(
  api: ApiRegistryDigestDocumentSection
): RegistryDigestDocumentSection {
  const sha256 = api.sha256;
  return {
    title: api.title ?? '',
    productCode: api.product_code ?? null,
    filename: api.filename ?? null,
    documentTitle: api.document_title ?? null,
    issued: api.issued ?? null,
    issuer: api.issuer ?? null,
    keyFacts: (api.key_facts ?? []).map((fact) => ({
      label: fact.label ?? '',
      value: fact.value ?? '',
      sourcePages: pageList(fact.source_pages),
    })),
    summary: api.summary ?? null,
    caveats: stringList(api.caveats),
    sha256: typeof sha256 === 'string' && sha256 ? sha256 : null,
    role: digestRoleOf(api.role),
    head: transformToRegistryDigestDocumentHead(api.head),
    corporate: transformToRegistryDigestCorporate(api.corporate),
    financials: api.financials ? transformToRegistryDigestFinancials(api.financials) : null,
    sources: (api.sources ?? []).map(transformToRegistryDigestSource),
    coverage: transformToRegistryDigestEntryCoverage(api),
  };
}

export function transformToRegistryDocumentDigest(
  api: ApiRegistryDocumentDigest
): RegistryDocumentDigest {
  const coverage = api.coverage;
  return {
    registration: transformToRegistryDigestRegistration(api.registration ?? emptyRegistration()),
    management: (api.management ?? []).map(transformToRegistryDigestOfficer),
    formerManagement: (api.former_management ?? []).map(transformToRegistryDigestOfficer),
    shareholders: (api.shareholders ?? []).map(transformToRegistryDigestShareholding),
    sources: (api.sources ?? []).map(transformToRegistryDigestSource),
    caveats: stringList(api.caveats),
    coverage: {
      documents: (coverage?.documents ?? []).map(transformToRegistryDigestDocumentCoverage),
      model: coverage?.model ?? null,
      chunks: countOr(coverage?.chunks, 0),
      chunksFailed: countOr(coverage?.chunks_failed, 0),
      generatedAt: coverage?.generated_at ?? null,
      officersDropped: countOr(coverage?.officers_dropped, 0),
    },
    schemaVersion: countOr(api.schema_version, 1),
    financials: api.financials ? transformToRegistryDigestFinancials(api.financials) : null,
    documents: (api.documents ?? []).map(transformToRegistryDigestDocumentSection),
    notices: stringList(api.notices),
  };
}

export function transformToRegistryDigestResult(
  api: ApiRegistryDigestResponse
): RegistryDigestResult {
  return {
    status: transformToRegistryDigestStatus(api.status),
    digest: api.digest ? transformToRegistryDocumentDigest(api.digest) : null,
  };
}

// ---------------------------------------------------------------------------
// Status vocabulary — the one place a status becomes a label and a colour
// ---------------------------------------------------------------------------

export interface RegistryOrderStatusMeta {
  label: string;
  color: string;
  /** What the status means for the analyst, as the pill's tooltip. */
  description: string;
}

export const REGISTRY_ORDER_STATUS_META: Record<RegistryOrderStatus, RegistryOrderStatusMeta> = {
  prepared: {
    label: 'Prepared',
    color: 'gray',
    description: 'Recorded on the report; sent to Armadillo when the report starts.',
  },
  pending: {
    label: 'Sending',
    color: 'yellow',
    description:
      'Sent and not yet confirmed. It may be live at Armadillo — confirm with the research desk before re-ordering.',
  },
  placed: {
    label: 'Placed',
    color: 'blue',
    description: 'Armadillo accepted the order; the documents are with its research team.',
  },
  partially_delivered: {
    label: 'Partly delivered',
    color: 'teal',
    description: 'Some of the ordered documents have arrived.',
  },
  delivered: {
    label: 'Delivered',
    color: 'green',
    description: 'Every ordered document has arrived.',
  },
  failed: {
    label: 'Failed',
    color: 'red',
    description: 'Nothing to wait for — see the reason on the order.',
  },
  timed_out: {
    label: 'Overdue',
    color: 'orange',
    description: 'Nothing arrived within the delivery window; chase the research desk.',
  },
  cancelled: {
    label: 'Cancelled',
    color: 'gray',
    description: 'Closed by a person; nothing more is expected.',
  },
};

/** Statuses that are live at Armadillo, may be, or will be sent. */
export const OPEN_REGISTRY_ORDER_STATUSES: readonly RegistryOrderStatus[] = [
  'prepared',
  'pending',
  'placed',
  'partially_delivered',
];

export function isRegistryOrderOpen(status: RegistryOrderStatus): boolean {
  return OPEN_REGISTRY_ORDER_STATUSES.includes(status);
}

/** Statuses a delivery can still move: the order is, or may be, live. */
const AWAITING_DELIVERY: ReadonlySet<RegistryOrderStatus> = new Set([
  'pending',
  'placed',
  'partially_delivered',
]);

/** The status to show. The delivery worker can run without permission to
 * move an order's status (shadow mode); it then records the status its
 * verdict would give, and that verdict is what an analyst should see.
 *
 * The verdict is the worker's own, never a count read here: a delivery it
 * held back for a person to check (the desk named another company, say)
 * is stored and counted too, and must not read as delivered.
 *
 * A status that settled the order (delivered, failed, timed out,
 * cancelled) always wins: a verdict recorded before a person closed the
 * order says nothing about it any more. */
export function effectiveRegistryOrderStatus(
  order: Pick<RegistryOrder, 'status' | 'proposedStatus'>
): RegistryOrderStatus {
  const proposed = order.proposedStatus;
  if (
    AWAITING_DELIVERY.has(order.status) &&
    (proposed === 'delivered' || proposed === 'partially_delivered')
  ) {
    return proposed;
  }
  return order.status;
}

/** Whether the order is past the date Armadillo's turnaround gave without
 * being delivered. A quote order has no such date and is never late.
 * Files that arrived but could not be matched to an ordered document do
 * not count as delivered, so a late order may still have files to open. */
export function isRegistryOrderLate(
  order: Pick<RegistryOrder, 'status' | 'proposedStatus' | 'expectedAt'>,
  now: Date = new Date()
): boolean {
  const status = effectiveRegistryOrderStatus(order);
  if (status !== 'placed' && status !== 'partially_delivered') {
    return false;
  }
  return order.expectedAt != null && new Date(order.expectedAt).getTime() < now.getTime();
}

/** Whether files may be stored against the order. Deliveries are kept for
 * every order that left Nexus, including one whose send was never
 * confirmed and one recorded as failed; only a prepared order, which
 * Armadillo has never seen, cannot have any. */
export function mayHaveRegistryDocuments(status: RegistryOrderStatus): boolean {
  return status !== 'prepared';
}

/** Whether a send of this order is in the air, so its row is about to
 * change: claimed and not yet answered (`pending`), or prepared on a
 * started report and never attempted — Start sends those within seconds.
 * A prepared order that has been attempted is waiting on the retry sweep,
 * which takes minutes, and is not in transit. */
export function isRegistryOrderInTransit(
  order: Pick<RegistryOrder, 'status' | 'placementAttempts'>,
  reportActivated: boolean
): boolean {
  if (order.status === 'pending') {
    return true;
  }
  return reportActivated && order.status === 'prepared' && order.placementAttempts === 0;
}

/** Report stages at which a prepared order is NOT sent yet — mirrors the
 * backend's `report_is_activated`: everything but triage, pending
 * activation and failed counts as activated. The client-facing
 * `submitted` stage is the pre-activation alias. */
const NOT_ACTIVATED_STAGES: ReadonlySet<string> = new Set([
  'triage',
  'pending_activation',
  'failed',
  'submitted',
]);

/** Whether a report at this stage sends prepared orders at once. Unknown
 * (null) reads as not activated: the safer message is "sent at Start". */
export function isReportActivatedForOrders(stage: ReportStatus | null | undefined): boolean {
  return stage != null && !NOT_ACTIVATED_STAGES.has(stage);
}

/** Whether ordering makes sense at this stage. A failed report never
 * starts, so an order prepared on it would sit unsent for ever. */
export function isReportOrderable(stage: ReportStatus | null | undefined): boolean {
  return stage !== 'failed';
}

/** What a status means to the analyst, as the pill's tooltip. `prepared`
 * depends on the report: once it has started, the order is no longer
 * waiting for Start but for the next send attempt. */
export function describeRegistryOrderStatus(
  order: Pick<RegistryOrder, 'status' | 'failureReason'>,
  reportActivated: boolean
): string {
  const { description } = REGISTRY_ORDER_STATUS_META[order.status];
  if (order.status === 'prepared' && reportActivated) {
    return 'Not at Armadillo yet. The report has started, so it is sent on the next attempt.';
  }
  const reason = registryOrderReasonLine(order);
  return reason ? `${description} ${reason}` : description;
}

/** The sentence an order that ended without its documents carries: why it
 * failed, or why a person closed it. Null for every other order. */
export function registryOrderReasonLine(
  order: Pick<RegistryOrder, 'status' | 'failureReason'>
): string | null {
  const ended = order.status === 'failed' || order.status === 'cancelled';
  return ended && order.failureReason ? order.failureReason : null;
}

/** An order's country for the `CountryPill`: the jurisdiction its code
 * names, or the bare code when it names none. */
export function registryOrderCountry(code: string): Country | string {
  return JURISDICTIONS.find((entry) => entry.alpha2code === code) ?? code;
}

/** A country's name for a sentence: the jurisdiction's name, or the bare
 * code when it names none. */
export function registryOrderCountryName(code: string): string {
  const country = registryOrderCountry(code);
  return typeof country === 'string' ? country : country.name;
}

/** Whether a quote order is waiting on its documents: it has no expected
 * date until the research desk has quoted. */
export function isRegistryOrderAwaitingQuote(
  order: Pick<RegistryOrder, 'status' | 'proposedStatus' | 'expectedAt' | 'requiresQuote'>
): boolean {
  const status = effectiveRegistryOrderStatus(order);
  return (
    order.expectedAt == null &&
    order.requiresQuote &&
    (status === 'placed' || status === 'partially_delivered')
  );
}

/** A price for display: the money, or "Quote" for a line the research team
 * prices first. Null when there is nothing to show. */
export function describeRegistryPrice(
  price: number | null,
  currency: string | null,
  requiresQuote: boolean
): string | null {
  if (requiresQuote && price == null) {
    return 'Quote';
  }
  if (price == null) {
    return null;
  }
  const code = currency ?? '';
  const symbol: Record<string, string> = { GBP: '£', EUR: '€', USD: '$' };
  const amount = price.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return symbol[code] ? `${symbol[code]}${amount}` : `${amount} ${code}`.trim();
}

// ---------------------------------------------------------------------------
// Desk replies and closing an order
// ---------------------------------------------------------------------------

/** The close reasons in the order the picker offers them. */
export const REGISTRY_ORDER_CLOSE_REASONS: readonly {
  value: RegistryOrderCloseReason;
  label: string;
}[] = [
  { value: 'quote_declined', label: 'Quote declined' },
  { value: 'cancelled_with_provider', label: 'Cancelled with Armadillo' },
  { value: 'not_received_by_provider', label: 'Armadillo has no record of the order' },
  { value: 'other', label: 'Other' },
];

/** The longest close note the API accepts. */
export const REGISTRY_ORDER_CLOSE_NOTE_MAX = 500;

/** Statuses a person can close: the order left Nexus, or may have. A
 * prepared order is discarded instead, and a settled one is not open. */
const CLOSEABLE_STATUSES: ReadonlySet<RegistryOrderStatus> = new Set(
  OPEN_REGISTRY_ORDER_STATUSES.filter((status) => status !== 'prepared')
);

type OrderCounts = Pick<RegistryOrder, 'receivedDocumentCount' | 'expectedDocumentCount'>;

function owesDocuments(order: OrderCounts): boolean {
  return order.receivedDocumentCount < order.expectedDocumentCount;
}

/** Whether "Close order" is offered. Mirrors the API's rule: an order
 * still open at Armadillo with documents owed. One whose documents have
 * all arrived is complete, not cancelled, even while its status still
 * reads placed (shadow mode). */
export function canCloseRegistryOrder(order: Pick<RegistryOrder, 'status'> & OrderCounts): boolean {
  return CLOSEABLE_STATUSES.has(order.status) && owesDocuments(order);
}

/** Whether the order carries a desk reply someone still has to act on:
 * the desk wrote without sending a document, nobody has answered it, and
 * the order is still open with documents owed. Once it is answered,
 * delivered or closed the reply is history. */
export function hasRegistryOrderReply(
  order: Pick<RegistryOrder, 'status' | 'replyCount'> & OrderCounts
): boolean {
  return order.replyCount > 0 && isRegistryOrderOpen(order.status) && owesDocuments(order);
}

// ---------------------------------------------------------------------------
// Replying to the desk
// ---------------------------------------------------------------------------

/** The longest reply the API accepts. */
export const REGISTRY_ORDER_REPLY_TEXT_MAX = 5000;

/** The two answers a quote usually gets, offered as one-click templates:
 * the button's label, and the text it puts in the box. */
export const REGISTRY_ORDER_REPLY_TEMPLATES: readonly { label: string; text: string }[] = [
  { label: 'Proceed with the order', text: 'Please proceed with the order.' },
  { label: 'Cancel this order', text: 'Please cancel this order.' },
];

/** Whether a reply to this message is offered. Mirrors the API's rules: a
 * prepared order was never sent, so there is nobody to answer; only the
 * desk's own messages are answered, and only from a sender the strict
 * rules accepted (an outbound message is this side's own). A message
 * without a recorded Message-ID cannot be named, so it cannot be answered
 * from here. */
export function canReplyToRegistryMessage(
  order: Pick<RegistryOrder, 'status'>,
  message: Pick<RegistryOrderMessage, 'direction' | 'senderVerified' | 'messageId'>
): boolean {
  if (order.status === 'prepared' || message.direction !== 'inbound') {
    return false;
  }
  return message.senderVerified && message.messageId !== null;
}

/** The longest note the API keeps with a quote recorded on its own. */
export const REGISTRY_ORDER_QUOTE_NOTE_MAX = 500;

/** Statuses the API lets a quote be recorded on: the order is live at
 * Armadillo. The stored status, as the API reads it: a `pending` order
 * (sent, not yet confirmed) is refused with a 409 whatever the delivery
 * worker's verdict on it says. */
const QUOTABLE_STATUSES: ReadonlySet<RegistryOrderStatus> = new Set([
  'placed',
  'partially_delivered',
]);

/** Whether the desk's quote may be recorded, with a reply or on its own.
 * Mirrors the API's rule: the order is live at Armadillo (placed, or
 * partly delivered), still owes documents, and has no expected date yet.
 * Recording one sets the date, so the order is chased when it is late; the
 * API refuses a second. Not only a quote order: one whose turnaround
 * Armadillo never gave benefits the same way.
 *
 * The worker's verdict counts on top of the stored status: an order it
 * would call delivered (shadow mode) is complete, and takes no quote. */
export function canRecordRegistryQuote(
  order: Pick<RegistryOrder, 'status' | 'proposedStatus' | 'expectedAt'> & OrderCounts
): boolean {
  if (!QUOTABLE_STATUSES.has(order.status)) {
    return false;
  }
  if (effectiveRegistryOrderStatus(order) === 'delivered') {
    return false;
  }
  return owesDocuments(order) && order.expectedAt == null;
}

/** The recorded quote for the price slot of a row: "Quoted £70.00". Null
 * when no quote, or none with a price, is recorded. */
export function describeRegistryQuote(quote: RegistryOrderQuote | null): string | null {
  const price = quote ? describeRegistryPrice(quote.price, quote.currency, false) : null;
  return price ? `Quoted ${price}` : null;
}

// ---------------------------------------------------------------------------
// Armadillo as a network partner: what each order adds to its fee
// ---------------------------------------------------------------------------

/** An amount in pounds with its pence: "£27.00". */
export function formatGbp(amount: number): string {
  return describeRegistryPrice(amount, 'GBP', false) ?? `£${amount}`;
}

/** What an order adds to Armadillo's partner fee, for a sentence: "£27.00
 * (list price)", "awaiting quote". Null when the API names no source (a
 * prepared order, or an API that predates the field). A figure in another
 * currency is a quote someone typed in: Armadillo's catalogue is priced in
 * pounds. */
export function describeRegistryFee(
  fee: number | null,
  source: RegistryFeeSource | null
): string | null {
  switch (source) {
    case 'list_price':
      return `${formatGbp(fee ?? 0)} (list price)`;
    case 'quote':
      return `${formatGbp(fee ?? 0)} (quote)`;
    case 'awaiting_quote':
      return 'awaiting quote';
    case 'not_gbp':
      return 'quote not in GBP: not billed automatically';
    case 'not_billable':
      return 'not billed: the order was closed or failed';
    default:
      return null;
  }
}

/** An order's "Partner fee" line: what it adds to Armadillo's fee. */
export function describePartnerFee(
  order: Pick<RegistryOrder, 'partnerFee' | 'partnerFeeSource'>
): string | null {
  return describeRegistryFee(order.partnerFee, order.partnerFeeSource);
}

/** Whether a partner assignment bills the order: it is linked to one and
 * adds to its fee, or will once quoted. A closed or failed order, or one
 * priced in another currency, stays linked yet adds nothing. */
export function isPartnerFeeBilled(
  order: Pick<RegistryOrder, 'backofficeAssignmentId' | 'partnerFeeSource'>
): boolean {
  return (
    order.backofficeAssignmentId !== null &&
    order.partnerFeeSource !== 'not_billable' &&
    order.partnerFeeSource !== 'not_gbp'
  );
}

// ---------------------------------------------------------------------------
// Mail that reached no order
// ---------------------------------------------------------------------------

/** The longest dismiss note the API accepts. */
export const REGISTRY_INBOUND_DISMISS_NOTE_MAX = 500;

/** Whether an email is still waiting for a person: on no order, and
 * neither attached nor dismissed. The row is decided by its own fields,
 * not by the view it was listed under. */
export function isRegistryInboundOpen(
  row: Pick<RegistryInboundRow, 'resolution' | 'orderId'>
): boolean {
  return row.resolution === null && row.orderId === null;
}

/** Plain words for the correlator's reason values an unmatched email
 * carries, keyed by the values of `Reason` in the API's
 * `delivery_correlate.py`. Anything this build does not know is shown as
 * its words. Exported for the test that keeps the keys in step with the
 * API: a key the API never emits is dead wording. */
export const REGISTRY_INBOUND_REASON_WORDS: Readonly<Record<string, string>> = {
  no_reference: 'No order reference',
  unknown_reference: 'Reference matches no order',
  ambiguous_reference: 'Names several orders',
  ambiguous_thread: 'Thread spans several orders',
  thread_match: 'In a known thread',
  reference_only_in_filename: 'Reference only in a file name',
  sender_unparseable: 'Sender could not be read',
  sender_not_allowed: 'Sender not on the list',
  sender_other_armadillo_address: 'Another Armadillo address',
  reference_from_untrusted_sender: 'Reference from an unverified sender',
  company_mismatch: 'Names another company',
  country_mismatch: 'Names another country',
  no_documents: 'No document attached',
};

export function describeRegistryInboundReason(reason: string): string {
  const known = REGISTRY_INBOUND_REASON_WORDS[reason];
  if (known) {
    return known;
  }
  if (reason.startsWith('authentication_')) {
    return 'Email authentication failed';
  }
  const words = reason.replace(/_/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : reason;
}

/** Why an order is suggested for an email, as the badge says it. */
export function describeSuggestionWhy(why: string): string {
  if (why === 'company_name') {
    return 'Same company name';
  }
  if (why === 'thread') {
    return 'Same email thread';
  }
  return why.replace(/_/g, ' ');
}

// ---------------------------------------------------------------------------
// Where the card shows
// ---------------------------------------------------------------------------

/** Where the registry-documents card shows and whether it offers new
 * orders. Carried as `registry_documents` on the report and the
 * report-request responses (`RegistryDocumentsScope` in
 * `vnext/schemas/registry_document.py`); the same shape on the wire and
 * in the app, so no transform. */
export interface RegistryDocumentsScope {
  /** The report has a live partner assignment locked to Armadillo i-doc,
   * the registry partner - one an analyst locked, or the automatic one -
   * so new orders are offered. The report's configuration plays no part:
   * another network partner may cover its corporate-records line, and
   * then nothing is ordered from Armadillo. */
  available: boolean;
  /** The report already has at least one order, of any status (cancelled
   * and closed included). Absent on an API that predates it. */
  has_orders?: boolean;
}

/** Whether the registry-documents card belongs on a report: where
 * Armadillo is locked on a live partner assignment, or where the report
 * already has orders (so they stay reachable once the lock goes). An API
 * without `available` (absent, not false) reads as available, so the
 * card keeps showing until the backend that gates it deploys; one without
 * `has_orders` reads as no orders. */
export function registryDocumentsShown(scope: RegistryDocumentsScope | null | undefined): boolean {
  return (scope?.available ?? true) || (scope?.has_orders ?? false);
}

/** Whether the card offers new orders: only while Armadillo is locked on
 * a live partner assignment. Existing orders keep every control they
 * have either way. Absent reads as available, as for
 * `registryDocumentsShown`. */
export function registryDocumentsOrderable(
  scope: RegistryDocumentsScope | null | undefined
): boolean {
  return scope?.available ?? true;
}

// ---------------------------------------------------------------------------
// Information from the delivered documents
// ---------------------------------------------------------------------------

/** Products whose documents the extraction does not read: a trace report
 * is what the desk sends when the document could not be obtained, and
 * gets a notice line instead. Every other PDF is read, filed accounts
 * included (into Financial Information). Mirrors the API's `TRACE` code. */
export const REGISTRY_DIGEST_EXCLUDED_PRODUCTS: ReadonlySet<string> = new Set(['TRACE']);

/** The word that makes a catalogue name or a delivered filename a trace
 * report ("TESCO PLC - Trace.pdf", "Trace Report"): a whole word, so a
 * "Traceability Certificate" is still read. Mirrors the API's
 * `TRACE_WORD`. */
const TRACE_WORD = 'trace';

/** The words of a name as the API's role rules read them: runs of
 * letters and digits, lower-cased ("ALL_DIR" is "all", "dir"). */
function nameWords(text: string | null | undefined): string[] {
  return (text ?? '').toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
}

/** The document part of a delivered filename: the text after the last
 * " - " without the extension ("TESCO PLC - Certificate of Good
 * Standing.pdf" names "Certificate of Good Standing"); the whole name when
 * it has no separator. The company name before the last " - " never names
 * the document. Mirrors the API's `document_part`. */
export function documentPartOfFilename(filename: string | null): string | null {
  const stem = filename?.trim().replace(/\.[a-z0-9]{1,5}$/i, '') ?? '';
  const part = stem.split(' - ').at(-1)?.trim();
  return part || null;
}

/** The catalogue lines an order was placed from: the names its products
 * go by. */
type RegistryCatalogueNames = readonly Pick<RegistryProduct, 'code' | 'description'>[];

/** Whether a delivered file is the desk's trace report rather than a
 * document: its product code is `TRACE`, or the catalogue's name for its
 * product or its filename's document part says "trace". A trace delivered
 * on an order for another product is stored without a code, so the
 * filename is read too. Mirrors the API's trace rule
 * (`digest_roles.document_role`). */
export function isRegistryTraceReport(
  file: Pick<RegistryDeliveredFile, 'productCode' | 'filename'>,
  catalogue: RegistryCatalogueNames = []
): boolean {
  const product = (file.productCode ?? '').trim().toUpperCase();
  if (REGISTRY_DIGEST_EXCLUDED_PRODUCTS.has(product)) {
    return true;
  }
  const name = product
    ? catalogue.find((line) => line.code.trim().toUpperCase() === product)?.description
    : null;
  return [...nameWords(name), ...nameWords(documentPartOfFilename(file.filename))].includes(
    TRACE_WORD
  );
}

/** Whether the extraction reads this file: a PDF that is not a trace
 * report. A file matched to no ordered product is read too. The product
 * code is trimmed and upper-cased before the comparison, as the API does,
 * so a file stored as "trace" is skipped on both sides. */
export function isRegistryDocumentDigestable(
  file: Pick<RegistryDeliveredFile, 'kind' | 'productCode' | 'filename'>,
  catalogue: RegistryCatalogueNames = []
): boolean {
  return file.kind === 'pdf' && !isRegistryTraceReport(file, catalogue);
}

/** Whether "Extract document information" is offered on an order: at
 * least one delivered file the API would read, so an accounts-only order
 * offers it and a trace-only one does not. Mirrors the API's rule, so the
 * button never leads to a `no_documents` refusal. The order's catalogue
 * snapshot names its products; without it, the code and the filename
 * decide. */
export function canDigestRegistryOrder(order: {
  documents: Pick<RegistryDeliveredFile, 'kind' | 'productCode' | 'filename'>[];
  catalogueSnapshot?: RegistryCatalogueNames;
}): boolean {
  return order.documents.some((file) =>
    isRegistryDocumentDigestable(file, order.catalogueSnapshot)
  );
}

function momentOrZero(value: string | null): number {
  const time = value ? new Date(value).getTime() : Number.NaN;
  return Number.isNaN(time) ? 0 : time;
}

/**
 * Of two readings of an order's extraction state, the one to show. The
 * state reaches the screen by several roads at once: the list row (read
 * every half minute or minute), the order's detail, what starting a run
 * answered and what polling learnt since. The later run wins, told apart
 * by when it was asked for; within one run the reading that has moved on
 * from `running` wins; otherwise the first argument does, so a caller
 * puts what it learnt itself first. Null only when both are.
 */
export function newerRegistryDigestStatus(
  preferred: RegistryDigestStatus | null | undefined,
  other: RegistryDigestStatus | null | undefined
): RegistryDigestStatus | null {
  if (!preferred) {
    return other ?? null;
  }
  if (!other) {
    return preferred;
  }
  const preferredAt = momentOrZero(preferred.requestedAt);
  const otherAt = momentOrZero(other.requestedAt);
  if (preferredAt !== otherAt) {
    return preferredAt > otherAt ? preferred : other;
  }
  return preferred.status === 'running' && other.status !== 'running' ? other : preferred;
}

/** What the "Add to report" control is on a completed digest: hidden
 * where the surface knows nothing of the report's document (an API that
 * predates the signal), the first add, or an update of the block added
 * earlier. A Hub-written (markdown) report takes the block as a Word
 * report does. */
export type RegistryDigestInsertionState = 'hidden' | 'add' | 'update';

export function registryDigestInsertionState(
  status: RegistryDigestStatus | null | undefined,
  reportContent: RegistryReportContent | null | undefined
): RegistryDigestInsertionState {
  if (!reportContent) {
    return 'hidden';
  }
  return status?.addedToReport ? 'update' : 'add';
}

/** The report stages at which content may be added: the API refuses the
 * rest (`report_stage`), so the control is disabled there. */
export const REPORT_STAGES_TAKING_CONTENT: readonly string[] = ['completed', 'in_qa'];

export function isReportStageTakingContent(lifecycleStage: string): boolean {
  return REPORT_STAGES_TAKING_CONTENT.includes(lifecycleStage);
}
